import { create } from "zustand";
import { persist } from "zustand/middleware";

const SHEET_MIN = 280;
const SHEET_MAX = 560;
const SHEET_DEFAULT = 360;

interface UiState {
  sidebarCollapsed: boolean;
  sheetOpen: boolean;
  sheetWidth: number;
  density: "comfortable" | "compact";
  artistsView: "table" | "explore" | "graph";
  /** Sidebar Spotify section list order. */
  spotifyPlaylistSort: "spotify" | "name";
  /** A6 — verbose API logging + inline codes; visibility only. */
  debugMode: boolean;
  /** Show the sheet Meta tab (pretty MusicBrainz / AcousticBrainz JSON). */
  showMetaTab: boolean;
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  setSheetOpen: (open: boolean) => void;
  setSheetWidth: (width: number) => void;
  setDensity: (density: "comfortable" | "compact") => void;
  setArtistsView: (view: "table" | "explore" | "graph") => void;
  setSpotifyPlaylistSort: (sort: "spotify" | "name") => void;
  setDebugMode: (on: boolean) => void;
  setShowMetaTab: (on: boolean) => void;
}

export const useUiStore = create<UiState>()(
  persist(
    (set) => ({
      sidebarCollapsed: false,
      sheetOpen: false,
      sheetWidth: SHEET_DEFAULT,
      density: "comfortable",
      artistsView: "table",
      spotifyPlaylistSort: "spotify",
      debugMode: false,
      showMetaTab: false,
      toggleSidebar: () => set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),
      setSidebarCollapsed: (sidebarCollapsed) => set({ sidebarCollapsed }),
      setSheetOpen: (sheetOpen) => set({ sheetOpen }),
      setSheetWidth: (width) =>
        set({ sheetWidth: Math.min(SHEET_MAX, Math.max(SHEET_MIN, Math.round(width))) }),
      setDensity: (density) => set({ density }),
      setArtistsView: (artistsView) => set({ artistsView }),
      setSpotifyPlaylistSort: (spotifyPlaylistSort) => set({ spotifyPlaylistSort }),
      setDebugMode: (debugMode) => set({ debugMode }),
      setShowMetaTab: (showMetaTab) => set({ showMetaTab }),
    }),
    { name: "coykoi-ui" },
  ),
);

export const SHEET_WIDTH = { min: SHEET_MIN, max: SHEET_MAX, default: SHEET_DEFAULT } as const;
